import { cap } from '../utils/format';
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, CheckCircle, Wrench, Mail, AlertCircle, XCircle, Eye, Download, Sparkles, Bell, Users, UserCheck, Landmark, ArrowUpCircle } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useAIFeatures } from '../hooks/useAIFeatures';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import EventTimeline from '../components/EventTimeline';
import BoardApprovalPanel, { BoardApprovalChip } from '../components/BoardApproval';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, MultiFilterSelect, PageHeader } from '../components/FilterBar';
import DateRangePicker from '../components/DateRangePicker';
import SearchableSelect from '../components/SearchableSelect';

const PRIORITIES = ['low','medium','high','critical'];

// Returns human-readable elapsed / response time for a ticket
function ticketElapsed(ticket) {
  const start = ticket.reported_date || ticket.created_at;
  if (!start) return null;
  const startMs  = new Date(start).getTime();
  const active   = ['open','in_progress','on_hold'].includes(ticket.status);
  const endMs    = active
    ? Date.now()
    : new Date(ticket.resolved_date || ticket.closed_date || ticket.updated_at).getTime();
  const totalMs  = endMs - startMs;
  if (totalMs < 0) return null;
  const hours    = totalMs / 3600000;
  const days     = Math.floor(hours / 24);
  const remHours = Math.floor(hours % 24);
  const mins     = Math.floor((totalMs % 3600000) / 60000);
  const label    = days > 0 ? `${days}d ${remHours}h` : hours >= 1 ? `${Math.floor(hours)}h ${mins}m` : `${mins}m`;
  // Color: green <8h, amber 8-48h, red >48h (for open), grey for resolved
  let color = '#10b981';
  if (!active) color = '#475569'; // resolved/closed — grey
  else if (hours > 48) color = '#ef4444';
  else if (hours >= 8)  color = '#f59e0b';
  return { label, color, hours, active };
}

function ElapsedBadge({ ticket }) {
  const el = ticketElapsed(ticket);
  if (!el) return null;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6,
      background: `${el.color}18`, color: el.color,
      border: `1px solid ${el.color}35`, whiteSpace: 'nowrap',
      display: 'inline-block', marginTop: 2,
    }}>
      {el.active ? '⏱ ' : '✓ '}{el.label}
    </span>
  );
}
const STATUSES = ['open','in_progress','on_hold','resolved','closed','pending_review'];
const FILTER_STATUSES = ['pending_review','open','in_progress','board_approval','on_hold','resolved','closed'];
// Who a ticket can be assigned to. Admin is included — the Head of IT takes
// tickets on personally and is who an IT staff member escalates to.
const DEFAULT_APPROVER = 'duminda@clsynergy.com';

// Logging work that is already finished: the KPIs measure resolved - reported,
// so the time spent is what sets the ticket's start, and nothing in the KPI
// queries has to know these tickets were entered after the fact.
const QUICK_DURATIONS = [
  { label: '15m', m: 15 }, { label: '30m', m: 30 }, { label: '1h', m: 60 },
  { label: '2h', m: 120 }, { label: '4h', m: 240 }, { label: '1 day', m: 480 },
];
// Local calendar date — toISOString() is UTC, which in Colombo (UTC+5:30)
// returns yesterday for the first 5½ hours of every day.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const nowHHMM = () => new Date().toTimeString().slice(0, 5);
const spentMinutes = (f) => (parseInt(f.spent_h || 0, 10) || 0) * 60 + (parseInt(f.spent_m || 0, 10) || 0);
const fmtDuration = (m) => {
  const h = Math.floor(m / 60), mm = m % 60;
  return [h && `${h}h`, mm && `${mm}m`].filter(Boolean).join(' ') || '0m';
};
const startedLabel = (f) => {
  const end = new Date(`${f.work_done_date || todayISO()}T${f.work_done_time || nowHHMM()}:00`);
  if (isNaN(end.getTime())) return '—';
  const start = new Date(end.getTime() - spentMinutes(f) * 60000);
  return start.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
const ROLE_LABEL = { admin: 'Head of IT', it_staff: 'IT Staff', junior_it: 'Junior IT' };
const staffOptions = (list) => (list || []).map(s => ({
  value: s.id, label: s.full_name, hint: ROLE_LABEL[s.role] || 'IT Staff',
}));

const CATEGORIES = ['hardware_issue','software_issue','network_issue','email_issue','access_request','printer_issue','phone_issue','security_incident','new_setup','data_recovery','backup_restore','other'];

// Canned ticket templates — pre-fill title/category/priority/description for common requests
const TICKET_TEMPLATES = [
  { label: 'Password Reset', title: 'Password reset request', category: 'access_request', priority: 'medium', description: 'Please reset my password for: [system name]\nUsername: [your username]' },
  { label: 'Laptop Running Slow', title: 'Laptop running slow / freezing', category: 'hardware_issue', priority: 'medium', description: 'My laptop has become slow / freezes.\nWhen it started: \nWhat I was doing at the time: ' },
  { label: 'No Internet / WiFi', title: 'Cannot connect to internet / WiFi', category: 'network_issue', priority: 'high', description: 'I cannot connect to the internet / WiFi.\nLocation (office/floor): \nOther devices affected: yes / no' },
  { label: 'Email Not Working', title: 'Email not sending / receiving', category: 'email_issue', priority: 'high', description: 'My email is not working.\nProblem: cannot send / cannot receive / both\nError message (if any): ' },
  { label: 'Printer Problem', title: 'Printer not working', category: 'printer_issue', priority: 'medium', description: 'Printer issue.\nPrinter location: \nProblem: not printing / paper jam / quality issue / toner' },
  { label: 'New Software Install', title: 'Software installation request', category: 'software_issue', priority: 'low', description: 'Please install the following software: [name]\nBusiness reason: ' },
  { label: 'System Access Request', title: 'Access request for system', category: 'access_request', priority: 'medium', description: 'Please grant me access to: [system name]\nAccess level needed: \nApproved by (manager): ' },
  { label: 'Phone / SIM Issue', title: 'Company phone or SIM issue', category: 'phone_issue', priority: 'medium', description: 'Issue with my company phone / SIM.\nPhone number: \nProblem: ' },
];

function TemplatePicker({ form, setForm }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Quick Template <span style={{color:'#334155'}}>(optional — pre-fills the form)</span></label>
      <select value="" onChange={e => {
        const t = TICKET_TEMPLATES[e.target.value];
        if (t) setForm(f => ({ ...f, title: t.title, category: t.category, priority: t.priority, description: t.description }));
      }} className="input-dark">
        <option value="">— Choose a common issue type —</option>
        {TICKET_TEMPLATES.map((t, i) => <option key={t.label} value={i}>{t.label}</option>)}
      </select>
    </div>
  );
}

// General user ticket form — loads their own assets/accessories for the dropdown
function UserTicketForm({ form, setForm, error, saving, employeeId, onSubmit, onClose }) {
  const [myAssets, setMyAssets] = useState([]);
  const [myAccs, setMyAccs] = useState([]);
  useEffect(() => {
    if (!employeeId) return;
    api.get('/assets', { params: { holder_id: employeeId, limit: 100 } }).then(r => setMyAssets(r.data.data || [])).catch(() => {});
    api.get('/accessories', { params: { limit: 100 } }).then(r => setMyAccs(r.data.data || [])).catch(() => {});
  }, [employeeId]);
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <ErrMsg msg={error}/>
      <TemplatePicker form={form} setForm={setForm}/>
      <FF label="Issue Title *" name="title" form={form} setForm={setForm} required placeholder="Briefly describe the issue"/>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Category</label>
          <select value={form.category||'hardware_issue'} onChange={e=>setForm({...form,category:e.target.value})} className="input-dark">
            {CATEGORIES.map(c=><option key={c} value={c}>{c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Priority</label>
          <select value={form.priority||'medium'} onChange={e=>setForm({...form,priority:e.target.value})} className="input-dark">
            {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Affected Asset / Accessory <span style={{color:'#334155'}}>(optional)</span></label>
        <select value={form.related_asset_id||''} onChange={e=>setForm({...form,related_asset_id:e.target.value||undefined})} className="input-dark">
          <option value="">— Select if related to a specific item —</option>
          {myAssets.length > 0 && <optgroup label="My Assets">{myAssets.map(a=><option key={a.id} value={a.id}>{a.asset_number} — {a.brand} {a.model}</option>)}</optgroup>}
          {myAccs.length > 0 && <optgroup label="My Accessories">{myAccs.map(a=><option key={a.id} value={`acc:${a.id}`}>{a.name||`${a.brand} ${a.model}`}{a.serial_number?` (S/N: ${a.serial_number})`:''}</option>)}</optgroup>}
        </select>
      </div>
      <FF label="Description *" name="description" form={form} setForm={setForm} rows={4} required placeholder="Describe the issue in detail..."/>
      <div className="flex gap-3">
        <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving ? 'Submitting...' : 'Submit Ticket'}</button>
      </div>
    </form>
  );
}

export default function Tickets() {
  const { isIT, isAdmin, isGeneralUser, isJuniorIT, user } = useAuth();
  // junior_it now uses the full IT ticket form (can raise tickets for any employee)
  const isSelfReporter = isGeneralUser || user?.role === 'director' || user?.role === 'finance_staff';
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterStatus, setFilterStatus] = useState([]); const [filterPriority, setFilterPriority] = useState([]); const [filterCategory, setFilterCategory] = useState([]); const [filterCompany, setFilterCompany] = useState([]); const [filterBoardApproval, setFilterBoardApproval] = useState([]);
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [pendingReviewRows, setPendingReviewRows] = useState([]); const [pendingReviewLoading, setPendingReviewLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [alsoApprove, setAlsoApprove] = useState(false);
  const [approvalForm, setApprovalForm] = useState({ estimation_cost: '', approver_email: DEFAULT_APPROVER, admin_remark: '', quotation: null });
  const [escalateModal, setEscalateModal] = useState(null);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'pending_review'
  const [reminderSending, setReminderSending] = useState({});
  const [assignModal, setAssignModal] = useState(null); const [assignTo, setAssignTo] = useState('');
  const [onBehalfOf, setOnBehalfOf] = useState(true); // Item 8 — on behalf of employee toggle
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [aiSuggestion, setAiSuggestion] = useState(''); const [aiSugLoading, setAiSugLoading] = useState(false);
  const [boardInfo, setBoardInfo] = useState(null); // latest board approval record for selected ticket
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const loadTimeline = async (id) => {
    setTimeline([]); setTimelineLoading(true);
    try { const r = await api.get(`/tickets/${id}/timeline`); setTimeline(r.data || []); }
    catch {} finally { setTimelineLoading(false); }
  };
  const aiFeatures = useAIFeatures();

  const getAiSuggestions = async () => {
    if (!selected?.id) return;
    setAiSugLoading(true); setAiSuggestion('');
    try {
      const r = await api.post('/ai/ticket-suggestions', { ticketId: selected.id });
      setAiSuggestion(r.data.suggestion);
    } catch (e) {
      setAiSuggestion('Could not get suggestions: ' + (e.response?.data?.error || e.message));
    } finally { setAiSugLoading(false); }
  };
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [empInfo, setEmpInfo] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);
  const [hodEmployees, setHodEmployees] = useState([]);
  const [itStaffList, setItStaffList] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (filterStatus.length) params.status = filterStatus.join(',');
      if (filterPriority.length) params.priority = filterPriority.join(',');
      if (filterCategory.length) params.category = filterCategory.join(',');
      if (filterCompany.length) params.company_id = filterCompany.join(',');
      if (filterBoardApproval.length) params.board_approval_status = filterBoardApproval.join(',');
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      const r = await api.get('/tickets', { params });
      setRows(r.data.tickets || r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterStatus, filterPriority, filterCategory, filterCompany, filterBoardApproval, dateFrom, dateTo]);

  const loadPendingReview = useCallback(async () => {
    if (!isAdmin && !(isIT && !isJuniorIT)) return; // junior_it lacks backend access to pending-review
    setPendingReviewLoading(true);
    try {
      const r = await api.get('/tickets/pending-review');
      setPendingReviewRows(r.data || []);
    } catch {} finally { setPendingReviewLoading(false); }
  }, [isAdmin, isIT, isJuniorIT]);

  const exportCSV = async () => {
    const params = { limit: 5000 };
    if (search) params.search = search;
    if (filterStatus.length) params.status = filterStatus.join(',');
    if (filterPriority.length) params.priority = filterPriority.join(',');
    if (filterCategory.length) params.category = filterCategory.join(',');
    if (filterCompany.length) params.company_id = filterCompany.join(',');
    if (filterBoardApproval.length) params.board_approval_status = filterBoardApproval.join(',');
    if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
    const r = await api.get('/tickets', { params });
    const data = r.data.tickets || r.data.data || [];
    const headers = ['Ticket #','Company','Title','Category','Priority','Status','Reported By','Asset','Reported Date','Resolved Date','Closed Date'];
    const rows2 = data.map(t => [
      t.ticket_number, t.company?.code||t.company?.name||'', t.title, cap(t.category), t.priority, t.status,
      t.reportedBy?.full_name||'', t.relatedAsset?.asset_number||'',
      t.reported_date ? new Date(t.reported_date).toLocaleDateString('en-GB') : '',
      t.resolved_date ? new Date(t.resolved_date).toLocaleDateString('en-GB') : '',
      t.closed_date ? new Date(t.closed_date).toLocaleDateString('en-GB') : ''
    ].map(v => `"${String(v).replace(/"/g,'""')}"`));
    const csv = [headers.join(','), ...rows2.map(r => r.join(','))].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `tickets_${dateFrom||'all'}_${dateTo||'all'}.csv`;
    a.click();
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPendingReview(); }, [loadPendingReview]);

  // Anything that changes a ticket can move it in or out of Pending Assignment,
  // so both lists are reloaded together. Editing a ticket to In Progress used to
  // refresh only the grid, leaving it listed under Pending Assignment until the
  // page was reloaded.
  const refreshTickets = useCallback(() => { load(); loadPendingReview(); }, [load, loadPendingReview]);
  useEffect(() => {
    api.get('/employees', { params: { limit: 1000, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {});
    api.get('/settings/companies').then(r => setCompanies(r.data || [])).catch(() => {});
    api.get('/settings/it-staff').then(r => setItStaffList(r.data || [])).catch(() => {});
  }, []);

  const handleEmployeeSelect = async (empId) => {
    setForm(prev => ({ ...prev, reported_by_employee_id: empId, related_asset_id: '', user_email_override: '' }));
    setEmpInfo(null);
    if (!empId) return;
    setEmpLoading(true);
    try {
      const info = await api.get(`/tickets/employee-info/${empId}`).then(r => r.data);
      setEmpInfo(info);
      // Pre-fill user email and company from employee record
      setForm(prev => ({ ...prev, user_email_override: info.email || '', reported_by_company_id: info.company_id || '' }));
      setHodEmployees(employees.filter(e => e.company_id === info.company_id && e.id !== empId));
    } catch {} finally { setEmpLoading(false); }
  };

  const handleAssetSelect = (assetId) => {
    const asset = empInfo?.assets?.find(a => a.id === assetId);
    setForm(prev => ({
      ...prev, related_asset_id: assetId,
      imei_info: asset?.imei_1 || '', serial_info: asset?.serial_number || ''
    }));
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (!selected) {
        const body = { ...form };
        if (!onBehalfOf) { delete body.reported_by_employee_id; delete body.related_asset_id; }
        if (body.close_immediately) {
          const mins = spentMinutes(body);
          if (mins < 1) { setError('Enter how long the job took — the KPIs are calculated from it'); setSaving(false); return; }
          body.duration_minutes = mins;
          // Send the moment itself. The server runs UTC, so a bare "15:00"
          // would be read as 15:00 UTC rather than 15:00 here.
          const finished = new Date(`${body.work_done_date || todayISO()}T${body.work_done_time || nowHHMM()}:00`);
          if (isNaN(finished.getTime())) { setError('The date and time the work finished are not valid'); setSaving(false); return; }
          if (finished.getTime() > Date.now() + 60000) { setError('The work cannot have finished in the future'); setSaving(false); return; }
          body.work_done_at = finished.toISOString();
        }
        delete body.spent_h; delete body.spent_m;
        await api.post('/tickets', body);
      } else {
        await api.put(`/tickets/${selected.id}`, form);
      }
      setModal(null); setSelected(null); setEmpInfo(null); refreshTickets();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const handleResolve = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post(`/tickets/${selected.id}/resolve`, form);
      setModal(null); setSelected(null); refreshTickets();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const handleSendReminder = async (ticket) => {
    setReminderSending(prev => ({ ...prev, [ticket.id]: true }));
    try {
      const r = await api.post(`/tickets/${ticket.id}/remind`);
      alert(r.data.message || 'Reminder sent');
    } catch (err) { alert(err.response?.data?.error || 'Failed to send reminder'); }
    finally { setReminderSending(prev => ({ ...prev, [ticket.id]: false })); }
  };

  const handleReviewAssign = async () => {
    if (!assignTo) return alert('Please select an IT staff member');
    if (alsoApprove) {
      if (!(parseFloat(approvalForm.estimation_cost) > 0)) return alert('Enter the estimated cost to send for board approval');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((approvalForm.approver_email || '').trim())) return alert("Enter the approver's email address");
    }
    setAssigning(true);
    try {
      await api.post(`/tickets/${assignModal.id}/review-assign`, { assigned_to: assignTo });
      // Sending it to the board from the same dialog saves reopening the
      // ticket just to do it — the assignment lands first, then the request.
      if (alsoApprove) {
        const fd = new FormData();
        fd.append('estimation_cost', approvalForm.estimation_cost);
        fd.append('approver_email', approvalForm.approver_email.trim());
        if (approvalForm.admin_remark) fd.append('admin_remark', approvalForm.admin_remark);
        if (approvalForm.quotation) fd.append('quotation', approvalForm.quotation);
        await api.post(`/tickets/${assignModal.id}/board-approval`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      closeAssign();
      refreshTickets();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
    finally { setAssigning(false); }
  };

  const closeAssign = () => {
    setAssignModal(null); setAssignTo(''); setAlsoApprove(false);
    setApprovalForm({ estimation_cost: '', approver_email: DEFAULT_APPROVER, admin_remark: '', quotation: null });
  };

  // An IT staff member handing a ticket they cannot resolve to the Head of IT
  const handleEscalate = async () => {
    if ((escalateReason || '').trim().length < 5) return alert('Please say briefly why you need higher support');
    setEscalating(true);
    try {
      const r = await api.post(`/tickets/${escalateModal.id}/escalate-to-admin`, { reason: escalateReason.trim() });
      setEscalateModal(null); setEscalateReason('');
      refreshTickets();
      alert(r.data.message + (r.data.emailed ? ' — the employee and HOD have been notified.' : ''));
    } catch (err) { alert(err.response?.data?.error || 'Failed to escalate'); }
    finally { setEscalating(false); }
  };

  const isOverdue = (ticket) => {
    if (!['open','in_progress'].includes(ticket.status)) return false;
    const created = new Date(ticket.created_at || ticket.reported_date);
    return (Date.now() - created.getTime()) > 2 * 24 * 60 * 60 * 1000;
  };

  const handleClose = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post(`/tickets/${selected.id}/close`, { remarks: form.remarks });
      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to close ticket'); } finally { setSaving(false); }
  };

  const selectedAsset = empInfo?.assets?.find(a => a.id === form.related_asset_id);

  const hodEmp = hodEmployees.find(e => e.id === form.hod_employee_id);

  const columns = [
    { header: 'Ticket #', render: r => <span className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{r.ticket_number}</span> },
    { header: 'Co.', render: r => r.company ? <span className="font-mono text-xs font-semibold" style={{color:'#818cf8'}}>{r.company.code}</span> : <span style={{color:'#334155'}}>—</span> },
    { header: 'Title', render: r => <div><div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{r.title}</div><div className="text-xs" style={{color:'#334155'}}>{r.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</div></div> },
    { header: 'Priority', render: r => <Badge value={r.priority}/> },
    { header: 'Status', render: r => (
      <div className="flex flex-col gap-1 items-start">
        <Badge value={r.status}/>
        <BoardApprovalChip status={r.board_approval_status}/>
      </div>
    )},
    { header: 'Reported By', render: r => <span style={{color:'#94a3b8'}}>{r.reportedBy?.full_name||'—'}</span> },
    { header: 'Assigned To', render: r => <span className="text-xs" style={{color: r.assignedUser ? '#a5b4fc' : '#334155'}}>{r.assignedUser?.full_name||'—'}</span> },
    { header: 'Asset', render: r => r.relatedAsset ? <span className="font-mono text-xs" style={{color:'#60a5fa'}}>{r.relatedAsset.asset_number}</span> : <span style={{color:'#334155'}}>—</span> },
    { header: 'Date / Time', render: r => (
      <div>
        <div className="text-xs" style={{color:'#475569'}}>{r.reported_date ? new Date(r.reported_date).toLocaleDateString('en-GB') : '—'}</div>
        <ElapsedBadge ticket={r}/>
      </div>
    )},
    { header: '', render: r => (
      <div className="flex gap-1">
        {/* Closed tickets: only admin can edit; IT staff cannot */}
        {isIT && (r.status !== 'closed' || isAdmin) && (
          <button onClick={() => { setSelected(r); setForm({...r, title: r.title, description: r.description, category: r.category, priority: r.priority, status: r.status, resolution_notes: r.resolution_notes||''}); setError(''); setModal('edit'); }}
            className="btn-ghost px-2 py-1 text-xs" title={r.status === 'closed' ? 'Admin only — ticket closed' : 'Edit'}>
            <Edit2 size={11}/>
          </button>
        )}
        {isIT && r.status !== 'resolved' && r.status !== 'closed' && (
          <button onClick={() => {
            setSelected(r);
            setForm({
              create_repair: false,
              repair_issue: r.description || '',
              repair_vendor: 'IT Department',
              repair_warranty_covered: false,
              repair_status: 'in_progress',
              is_physical_damage: false,
              damage_description: r.description || '',
              damage_repair_cost: r.damage_repair_cost || '',
              damage_payment_method: 'user_pay',
              damage_paid_by: 'user',
            });
            setError(''); setAiSuggestion(''); setModal('resolve');
          }} className="btn-ghost px-2 py-1 text-xs" style={{color:'#10b981'}} title="Mark Resolved">
            <CheckCircle size={11}/>
          </button>
        )}
        {isAdmin && r.status === 'resolved' && (
          <button onClick={() => { setSelected(r); setForm({remarks:''}); setError(''); setModal('close'); }}
            className="btn-ghost px-2 py-1 text-xs" style={{color:'#60a5fa'}} title="Close Ticket (Admin only)">
            <XCircle size={11}/>
          </button>
        )}
        {isAdmin && ['open','in_progress','on_hold','board_approval'].includes(r.status) && (
          <button onClick={async () => {
            setSelected(r); setError('');
            setForm({ estimation_cost: '', quotation: null, admin_remark: '' });
            setBoardInfo(null); setModal('board');
            try { const br = await api.get(`/tickets/${r.id}/board-approval`); setBoardInfo(br.data); } catch {}
          }} className="btn-ghost px-2 py-1 text-xs" style={{color:'#a78bfa'}} title="Board Approval — estimated cost sign-off">
            <Landmark size={11}/>
          </button>
        )}
        {/* IT staff can hand a ticket they cannot resolve to the Head of IT */}
        {isIT && !isAdmin && ['open','in_progress','on_hold'].includes(r.status) && !r.higher_support_at && (
          <button onClick={() => { setEscalateModal(r); setEscalateReason(''); }}
            className="btn-ghost px-2 py-1 text-xs" style={{color:'#a78bfa'}}
            title="Escalate to the Head of IT for higher support">
            <ArrowUpCircle size={11}/>
          </button>
        )}
        {r.higher_support_at && (
          <span title="Escalated to the Head of IT for higher support"
            style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:5,whiteSpace:'nowrap',
              background:'rgba(139,92,246,0.14)',color:'#a78bfa',border:'1px solid rgba(139,92,246,0.3)'}}>
            ↑ Head of IT
          </span>
        )}
        {isAdmin && isOverdue(r) && r.assigned_to && (
          <button onClick={() => handleSendReminder(r)} disabled={reminderSending[r.id]}
            className="btn-ghost px-2 py-1 text-xs" style={{color:'#f59e0b'}} title="Send Overdue Reminder">
            {reminderSending[r.id] ? <RefreshCw size={11} className="animate-spin"/> : <Bell size={11}/>}
          </button>
        )}
        {(r.status === 'resolved' || r.status === 'closed') && (
          <button onClick={() => { setSelected(r); setModal('view'); loadTimeline(r.id); }}
            className="btn-ghost px-2 py-1 text-xs" style={{color:'#94a3b8'}} title="View Full Details">
            <Eye size={11}/>
          </button>
        )}
      </div>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="IT Tickets" count={total}>
        <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
        {isIT && <button onClick={exportCSV} className="btn-ghost p-2" title="Export CSV"><Download size={13}/></button>}
        {(isIT || isJuniorIT) && <button onClick={() => { setSelected(null); setEmpInfo(null); setForm({priority:'medium',status:'open',category:'hardware_issue',estimated_days:'2',assigned_to:user?.id||''}); setError(''); setOnBehalfOf(true); setModal('new'); }} className="btn-primary"><Plus size={13}/> New Ticket</button>}
        {isSelfReporter && <button onClick={() => { setSelected(null); setForm({priority:'medium',status:'open',category:'hardware_issue',reported_by_employee_id:user.employee_id||undefined}); setError(''); setModal('user-ticket'); }} className="btn-primary"><Plus size={13}/> Raise Ticket</button>}
      </PageHeader>

      {/* Pending Review tab (admin/it_staff only — backend 403s junior_it) */}
      {(isAdmin || (isIT && !isJuniorIT)) && (
        <div className="flex gap-2">
          <button onClick={() => setActiveTab('all')}
            className={`text-xs px-4 py-1.5 rounded-lg font-medium transition-all ${activeTab==='all' ? 'text-white' : 'btn-ghost'}`}
            style={activeTab==='all' ? {background:'rgba(99,102,241,0.25)',border:'1px solid rgba(99,102,241,0.4)',color:'#818cf8'} : {}}>
            All Tickets
          </button>
          <button onClick={() => setActiveTab('pending_review')}
            className={`text-xs px-4 py-1.5 rounded-lg font-medium transition-all flex items-center gap-1.5 ${activeTab==='pending_review' ? 'text-white' : 'btn-ghost'}`}
            style={activeTab==='pending_review' ? {background:'rgba(245,158,11,0.2)',border:'1px solid rgba(245,158,11,0.4)',color:'#fbbf24'} : {}}>
            <AlertCircle size={11}/>
            Pending Assignment
            {pendingReviewRows.length > 0 && (
              <span className="rounded-full px-1.5 py-0.5 text-xs font-bold" style={{background:'rgba(245,158,11,0.3)',color:'#fbbf24',fontSize:10}}>
                {pendingReviewRows.length}
              </span>
            )}
          </button>
        </div>
      )}

      {activeTab === 'pending_review' ? (
        <div className="glass glow-border rounded-2xl overflow-hidden">
          {pendingReviewLoading ? (
            <div className="flex items-center justify-center p-12 gap-2" style={{color:'#334155'}}>
              <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>Loading...
            </div>
          ) : pendingReviewRows.length === 0 ? (
            <div className="text-center p-10" style={{color:'#334155'}}>No tickets pending assignment.</div>
          ) : (
            <table className="w-full" style={{fontSize:13}}>
              <thead><tr style={{borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                {['Ticket #','Employee','Issue','Category','Priority','Date','Assign To'].map(h=>(
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{color:'#475569'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{pendingReviewRows.map(r => (
                <tr key={r.id} style={{borderBottom:'1px solid rgba(255,255,255,0.04)',background:'rgba(245,158,11,0.04)'}}>
                  <td className="px-4 py-3"><span className="font-mono text-xs" style={{color:'#60a5fa'}}>{r.ticket_number}</span></td>
                  <td className="px-4 py-3 text-xs" style={{color:'#94a3b8'}}>{r.reportedBy?.full_name||'—'}</td>
                  <td className="px-4 py-3 text-xs font-medium" style={{color:'#e2e8f0',maxWidth:200}}>{r.title}</td>
                  <td className="px-4 py-3 text-xs capitalize" style={{color:'#475569'}}>{(r.category||'').replace(/_/g,' ')}</td>
                  <td className="px-4 py-3"><Badge value={r.priority}/></td>
                  <td className="px-4 py-3 text-xs" style={{color:'#475569'}}>{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—'}</td>
                  <td className="px-4 py-3" colSpan={2}>
                    <div className="flex items-center gap-2">
                      <div style={{minWidth:150}}>
                        <SearchableSelect value="" onChange={v => { if (v) { setAssignTo(v); setAssignModal(r); } }}
                          options={staffOptions(itStaffList)} placeholder="Assign to..."/>
                      </div>
                      <button onClick={() => { setAssignModal(r); }} className="btn-ghost px-2 py-1 text-xs" style={{color:'#818cf8'}} title="Open assign dialog">
                        <UserCheck size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      ) : (
        <>
          <FilterBar hasFilters={!!(search||filterStatus.length||filterPriority.length||filterCategory.length||filterCompany.length||filterBoardApproval.length||dateFrom||dateTo)} onClear={() => { setSearch(''); setFilterStatus([]); setFilterPriority([]); setFilterCategory([]); setFilterCompany([]); setFilterBoardApproval([]); setDateFrom(''); setDateTo(''); setPage(1); }}>
            <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search ticket #, title, description, employee name..."/>
            <MultiFilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={FILTER_STATUSES.map(s=>({value:s,label:s.replace(/_/g,' ')}))}/>
            <MultiFilterSelect value={filterPriority} onChange={v => { setFilterPriority(v); setPage(1); }} placeholder="All Priorities" options={PRIORITIES.map(p=>({value:p,label:p}))}/>
            <MultiFilterSelect value={filterCategory} onChange={v => { setFilterCategory(v); setPage(1); }} placeholder="All Categories" options={CATEGORIES.map(c=>({value:c,label:c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}))}/>
            {isAdmin && <MultiFilterSelect value={filterBoardApproval} onChange={v => { setFilterBoardApproval(v); setPage(1); }} placeholder="Board Approval" options={[{value:'pending',label:'Pending'},{value:'approved',label:'Approved'},{value:'rejected',label:'Rejected'}]}/>}
            {isIT && <MultiFilterSelect value={filterCompany} onChange={v => { setFilterCompany(v); setPage(1); }} placeholder="All Companies" options={companies.map(c=>({value:c.id,label:c.name}))}/>}
            <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
          </FilterBar>
          <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}
            rowStyle={r => isOverdue(r) ? {background:'rgba(245,158,11,0.05)',borderLeft:'2px solid rgba(245,158,11,0.4)'} : {}}/>
        </>
      )}

      {/* ── NEW TICKET ─────────────────────────────── */}
      {modal === 'new' && (
        <Modal title="New IT Ticket" onClose={() => { setModal(null); setEmpInfo(null); }} size="xl">
          <form onSubmit={handleSave} className="space-y-5">
            <ErrMsg msg={error}/>

            {/* Item 8 — On behalf of employee toggle */}
            <div className="flex items-center gap-3 px-1">
              <label className="flex items-center gap-2 cursor-pointer text-sm" style={{color:'#94a3b8'}}>
                <input type="checkbox" checked={onBehalfOf} onChange={e=>{ setOnBehalfOf(e.target.checked); if(!e.target.checked){ setEmpInfo(null); setForm(f=>({...f,reported_by_employee_id:'',related_asset_id:''})); } }} className="accent-indigo-500 w-4 h-4"/>
                Raised on behalf of an employee
              </label>
              <span className="text-xs" style={{color:'#334155'}}>{onBehalfOf ? 'Select the employee this ticket is for' : 'Direct IT issue — no specific employee'}</span>
            </div>

            {/* Step 1: Employee */}
            {onBehalfOf && (
            <div className="glass rounded-xl p-4" style={{border:'1px solid rgba(59,130,246,0.2)'}}>
              <div className="text-xs font-semibold mb-3" style={{color:'#60a5fa'}}>Step 1 — Select Employee</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Employee *</label>
                  <SearchableSelect required value={form.reported_by_employee_id||''}
                    onChange={v => handleEmployeeSelect(v)}
                    options={employees.map(e => ({ value: e.id, label: e.full_name, hint: e.employee_code || undefined }))}
                    placeholder="Select employee..."/>
                </div>
                {empInfo && (
                  <div className="rounded-xl p-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                    <div className="font-semibold text-sm" style={{color:'#e2e8f0'}}>{empInfo.full_name}</div>
                    <div className="text-xs mt-1" style={{color:'#475569'}}>{empInfo.department?.name} · {empInfo.company?.name}</div>
                    {empInfo.company && (
                      <div className="mt-1 text-xs px-2 py-0.5 inline-block rounded font-mono font-semibold" style={{background:'rgba(129,140,248,0.15)',color:'#818cf8'}}>
                        Company: {empInfo.company.code || empInfo.company.name}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Email overrides — FIX: allow manual email entry when DB has no email */}
              {empInfo && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>
                      User Email for notification
                      {!empInfo.email && <span className="ml-2 text-xs" style={{color:'#f59e0b'}}>⚠ No email on file — enter manually</span>}
                    </label>
                    <input value={form.user_email_override||''} onChange={e=>setForm({...form,user_email_override:e.target.value})}
                      className="input-dark" type="email" placeholder={empInfo.email ? empInfo.email : 'Enter user email address...'}/>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>HOD (Head of Department)</label>
                    <SearchableSelect value={form.hod_employee_id||''}
                      onChange={v => setForm({...form, hod_employee_id: v, hod_email_override: ''})}
                      options={hodEmployees.map(e => ({ value: e.id, label: e.full_name, hint: e.designation || undefined }))}
                      placeholder="Select HOD (optional)"/>
                  </div>
                  {form.hod_employee_id && !hodEmp?.email && (
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{color:'#f59e0b'}}>⚠ HOD email (not on file — enter manually)</label>
                      <input value={form.hod_email_override||''} onChange={e=>setForm({...form,hod_email_override:e.target.value})}
                        className="input-dark" type="email" placeholder="Enter HOD email..."/>
                    </div>
                  )}
                </div>
              )}
            </div>
            )} {/* end onBehalfOf */}

            {/* Step 2: Asset */}
            {onBehalfOf && empInfo && (
              <div className="glass rounded-xl p-4" style={{border:'1px solid rgba(16,185,129,0.2)'}}>
                <div className="text-xs font-semibold mb-3" style={{color:'#34d399'}}>Step 2 — Select Asset (optional)</div>
                {empLoading ? <div style={{color:'#334155',fontSize:12}}>Loading assets...</div> :
                  empInfo.assets?.length > 0 ? (
                    <div>
                      <select value={form.related_asset_id||''} onChange={e=>handleAssetSelect(e.target.value)} className="input-dark">
                        <option value="">No specific asset</option>
                        {empInfo.assets.map(a=>(
                          <option key={a.id} value={a.id}>{a.brand} {a.model} — {a.asset_number}{a.imei_1?` (IMEI: ${a.imei_1})`:''}</option>
                        ))}
                      </select>
                      {selectedAsset && (
                        <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                          {[['Asset #', selectedAsset.asset_number, '#60a5fa'],['IMEI', selectedAsset.imei_1||'—','#e2e8f0'],['Serial', selectedAsset.serial_number||'—','#e2e8f0']].map(([k,v,c])=>(
                            <div key={k} className="rounded-lg p-2" style={{background:'rgba(255,255,255,0.03)'}}>
                              <div style={{color:'#334155'}}>{k}</div>
                              <div className="font-mono font-medium" style={{color:c}}>{v}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : <div className="text-xs" style={{color:'#334155'}}>No assets currently assigned to this employee</div>
                }
              </div>
            )}

            {/* Ticket details */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><TemplatePicker form={form} setForm={setForm}/></div>
              <div className="col-span-2"><FF label="Issue Title *" name="title" form={form} setForm={setForm} required placeholder="Brief description of the issue"/></div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Category</label>
                <select value={form.category||'hardware_issue'} onChange={e=>setForm({...form,category:e.target.value})} className="input-dark">
                  {CATEGORIES.map(c=><option key={c} value={c}>{c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Priority</label>
                <select value={form.priority||'medium'} onChange={e=>setForm({...form,priority:e.target.value})} className="input-dark">
                  {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <FF label="Estimated Resolution (working days)" name="estimated_days" type="number" form={form} setForm={setForm}/>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Physical Damage?</label>
                <select value={form.is_physical_damage?'yes':'no'} onChange={e=>setForm({...form,is_physical_damage:e.target.value==='yes'})} className="input-dark">
                  <option value="no">No</option>
                  <option value="yes">Yes — NOTE included in email</option>
                </select>
              </div>
            </div>
            <FF label="Issue Details *" name="description" form={form} setForm={setForm} rows={4} required placeholder="Describe the issue in full detail..."/>
            <FF label="Remark" name="remarks" form={form} setForm={setForm} rows={2} placeholder="e.g. Hand over the device"/>

            {/* Assign To */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Assign To (IT Staff)</label>
              <SearchableSelect value={form.assigned_to||''}
                onChange={v => setForm({...form, assigned_to: v || user?.id || ''})}
                options={staffOptions(itStaffList)} placeholder="Assign to me (default)"/>
            </div>

            {/* Already done — logged afterwards so it counts in the IT KPIs */}
            {isIT && (
              <div className="rounded-xl p-3" style={{background: form.close_immediately ? 'rgba(16,185,129,0.07)' : 'rgba(255,255,255,0.03)', border: `1px solid ${form.close_immediately ? 'rgba(16,185,129,0.25)' : 'rgba(255,255,255,0.06)'}`}}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.close_immediately}
                    onChange={e => setForm({...form, close_immediately: e.target.checked,
                      work_done_date: form.work_done_date || todayISO(),
                      work_done_time: form.work_done_time || nowHHMM(),
                    })}
                    className="accent-emerald-500"/>
                  <span className="text-xs font-semibold" style={{color: form.close_immediately ? '#34d399' : '#94a3b8'}}>
                    <CheckCircle size={11} style={{display:'inline',marginRight:4}}/>
                    Already done — log it and close straight away
                  </span>
                </label>
                <div className="text-xs mt-1.5" style={{color:'#334155'}}>
                  For day-to-day work you have already handled, so it counts towards the IT KPIs. No email is sent to anyone.
                </div>

                {form.close_immediately && (
                  <div className="mt-3 space-y-2.5">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs mb-1" style={{color:'#475569'}}>Finished on *</label>
                        <input type="date" max={todayISO()} value={form.work_done_date || todayISO()}
                          onChange={e => setForm({...form, work_done_date: e.target.value})} className="input-dark"/>
                      </div>
                      <div>
                        <label className="block text-xs mb-1" style={{color:'#475569'}}>at</label>
                        <input type="time" value={form.work_done_time || nowHHMM()}
                          onChange={e => setForm({...form, work_done_time: e.target.value})} className="input-dark"/>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs mb-1" style={{color:'#475569'}}>How long did it take? *</label>
                      <div className="flex gap-2 items-center">
                        <input type="number" min="0" max="720" placeholder="0" style={{width:70}}
                          value={form.spent_h ?? ''} onChange={e => setForm({...form, spent_h: e.target.value})} className="input-dark"/>
                        <span className="text-xs" style={{color:'#475569'}}>hours</span>
                        <input type="number" min="0" max="59" placeholder="0" style={{width:70}}
                          value={form.spent_m ?? ''} onChange={e => setForm({...form, spent_m: e.target.value})} className="input-dark"/>
                        <span className="text-xs" style={{color:'#475569'}}>minutes</span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap mt-2">
                        {QUICK_DURATIONS.map(q => {
                          const on = (parseInt(form.spent_h||0,10) * 60 + parseInt(form.spent_m||0,10)) === q.m;
                          return (
                            <button key={q.label} type="button"
                              onClick={() => setForm({...form, spent_h: String(Math.floor(q.m/60)), spent_m: String(q.m%60)})}
                              style={{fontSize:10,fontWeight:600,padding:'3px 8px',borderRadius:5,cursor:'pointer',
                                background: on ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.04)',
                                border:`1px solid ${on ? 'rgba(52,211,153,0.4)' : 'rgba(255,255,255,0.08)'}`,
                                color: on ? '#34d399' : '#94a3b8'}}>{q.label}</button>
                          );
                        })}
                      </div>
                      <div className="text-xs mt-2" style={{color:'#334155'}}>
                        This is what the KPIs count as the resolution time, measured back from the finish time
                        {spentMinutes(form) > 0 && (
                          <> — recorded as received <strong style={{color:'#94a3b8'}}>{startedLabel(form)}</strong>, resolved {fmtDuration(spentMinutes(form))} later.</>
                        )}
                      </div>
                    </div>

                    <FF label="What was done (resolution note)" name="resolution_notes" form={form} setForm={setForm} rows={2}
                      placeholder="Defaults to the issue details if left blank"/>
                  </div>
                )}
              </div>
            )}

            {/* Email summary */}
            {onBehalfOf && empInfo && form.title && !form.close_immediately && (
              <div className="rounded-xl p-3" style={{background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.2)'}}>
                <div className="flex items-center gap-2 mb-2"><Mail size={13} style={{color:'#818cf8'}}/><span className="text-xs font-semibold" style={{color:'#818cf8'}}>Email will be sent to:</span></div>
                <div className="text-xs space-y-1" style={{color:'#94a3b8'}}>
                  <div>• {empInfo.full_name} — {form.user_email_override || empInfo.email || <span style={{color:'#f87171'}}>No email — please enter above</span>}</div>
                  {form.hod_employee_id && <div>• HOD: {hodEmp?.full_name} — {form.hod_email_override || hodEmp?.email || <span style={{color:'#f59e0b'}}>Enter HOD email above</span>}</div>}
                  <div>• IT Dept (CC)</div>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setEmpInfo(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center"
                style={form.close_immediately ? {background:'linear-gradient(135deg,#10b981,#059669)'} : undefined}>
                {saving ? (form.close_immediately ? 'Logging...' : 'Creating & Sending Email...')
                        : (form.close_immediately ? 'Log as Completed' : 'Create Ticket & Send Email')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── EDIT TICKET — new clean format ────────── */}
      {modal === 'edit' && (
        <Modal title={`Edit Ticket: ${selected?.ticket_number}`} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error}/>

            {/* Ticket info banner */}
            <div className="px-4 py-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="text-xs" style={{color:'#475569'}}>
                Reporter: <span style={{color:'#e2e8f0'}}>{selected?.reportedBy?.full_name||'—'}</span>
                {selected?.relatedAsset && <> · Asset: <span className="font-mono" style={{color:'#60a5fa'}}>{selected.relatedAsset.asset_number}</span></>}
                · Reported: <span style={{color:'#e2e8f0'}}>{selected?.reported_date ? new Date(selected.reported_date).toLocaleDateString() : '—'}</span>
                {selected && <> · <ElapsedBadge ticket={selected}/></>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><FF label="Title *" name="title" form={form} setForm={setForm} required/></div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Category</label>
                <select value={form.category||'hardware_issue'} onChange={e=>setForm({...form,category:e.target.value})} className="input-dark">
                  {CATEGORIES.map(c=><option key={c} value={c}>{c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Priority</label>
                <select value={form.priority||'medium'} onChange={e=>setForm({...form,priority:e.target.value})} className="input-dark">
                  {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Status</label>
                <select value={form.status||'open'} onChange={e=>setForm({...form,status:e.target.value})} className="input-dark">
                  {STATUSES.map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
                </select>
              </div>
            </div>
            <FF label="Description" name="description" form={form} setForm={setForm} rows={4}/>
            <FF label="Resolution Notes" name="resolution_notes" form={form} setForm={setForm} rows={3} placeholder="How was the issue resolved?"/>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Assign To (IT Staff)</label>
              <SearchableSelect value={form.assigned_to||''}
                onChange={v => setForm({...form, assigned_to: v || undefined})}
                options={staffOptions(itStaffList)} placeholder="— Keep current assignment —"/>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save Changes'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── RESOLVE TICKET ────────────────────────── */}
      {/* General user raise ticket modal */}
      {modal === 'user-ticket' && (
        <Modal title="Raise IT Support Ticket" onClose={() => { setModal(null); setForm({}); }} size="lg">
          <UserTicketForm
            form={form} setForm={setForm} error={error} saving={saving}
            employeeId={user?.employee_id}
            onSubmit={async e => {
              e.preventDefault(); setSaving(true); setError('');
              try {
                let assetId = form.related_asset_id;
                let desc = form.description || '';
                if (assetId?.startsWith('acc:')) {
                  desc += `\n\n[Affected Accessory ID: ${assetId.replace('acc:', '')}]`;
                  assetId = undefined;
                }
                await api.post('/tickets', {
                  title: form.title, description: desc,
                  category: form.category || 'hardware_issue',
                  priority: form.priority || 'medium',
                  status: 'open',
                  reported_by_employee_id: user?.employee_id,
                  related_asset_id: assetId || undefined,
                });
                setModal(null); setForm({}); load();
              } catch (err) { setError(err.response?.data?.error || 'Failed to submit'); } finally { setSaving(false); }
            }}
            onClose={() => { setModal(null); setForm({}); }}
          />
        </Modal>
      )}

      {modal === 'resolve' && (
        <Modal title={`Resolve: ${selected?.ticket_number}`} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <form onSubmit={handleResolve} className="space-y-4">
            <ErrMsg msg={error}/>

            {/* Ticket summary */}
            <div className="px-4 py-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="font-medium text-sm mb-1" style={{color:'#e2e8f0'}}>{selected?.title}</div>
              <div className="text-xs" style={{color:'#475569'}}>
                {selected?.reportedBy?.full_name||'—'}
                {selected?.relatedAsset && <> · <span className="font-mono" style={{color:'#60a5fa'}}>{selected.relatedAsset.asset_number}</span></>}
              </div>
            </div>

            {/* AI Suggestions */}
            {aiFeatures.suggestions && <div className="rounded-xl p-3" style={{ background:'rgba(139,92,246,0.07)', border:'1px solid rgba(139,92,246,0.2)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color:'#a78bfa' }}>
                  <Sparkles size={12}/> AI Resolution Suggestions
                </div>
                <button type="button" onClick={getAiSuggestions} disabled={aiSugLoading}
                  className="text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5"
                  style={{ background:'rgba(139,92,246,0.2)', color:'#c084fc', border:'1px solid rgba(139,92,246,0.3)', opacity: aiSugLoading ? 0.7 : 1 }}>
                  {aiSugLoading ? <RefreshCw size={11} className="animate-spin"/> : <Sparkles size={11}/>}
                  {aiSugLoading ? 'Getting suggestions...' : 'Get AI Suggestions'}
                </button>
              </div>
              {aiSuggestion && (
                <div className="mt-3 space-y-2">
                  <pre className="text-xs whitespace-pre-wrap" style={{ color:'#cbd5e1', fontFamily:'inherit', lineHeight:1.6 }}>{aiSuggestion}</pre>
                  <button type="button"
                    onClick={() => setForm(f => ({ ...f, resolution_notes: (f.resolution_notes ? f.resolution_notes + '\n\n' : '') + aiSuggestion }))}
                    className="text-xs px-2.5 py-1 rounded-lg"
                    style={{ background:'rgba(16,185,129,0.15)', color:'#34d399', border:'1px solid rgba(16,185,129,0.3)' }}>
                    ↓ Use this as resolution notes
                  </button>
                </div>
              )}
            </div>

            }
            <FF label="Resolution Notes *" name="resolution_notes" form={form} setForm={setForm} rows={4} required placeholder="Describe how the issue was resolved..."/>
            <FF label="Root Cause" name="root_cause" form={form} setForm={setForm} rows={2} placeholder="What caused the issue?"/>

            {/* ── Add to IT Repair ── */}
            <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(139,92,246,0.25)'}}>
              <label className="flex items-center gap-2 cursor-pointer px-4 py-3" style={{background:'rgba(139,92,246,0.10)'}}>
                <input type="checkbox" checked={!!form.create_repair} onChange={e=>setForm({...form,create_repair:e.target.checked})} className="accent-purple-500"/>
                <Wrench size={14} style={{color:'#c084fc'}}/> <span className="text-sm font-medium" style={{color:'#c084fc'}}>Add to IT Repairs</span>
              </label>
              {form.create_repair && (
                <div className="px-4 pb-4 pt-3 space-y-3" style={{background:'rgba(139,92,246,0.04)'}}>
                  {/* Read-only asset + pre-filled issue */}
                  {selected?.relatedAsset && (
                    <div className="px-3 py-2 rounded-lg text-xs" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                      <span style={{color:'#475569'}}>Asset: </span>
                      <span className="font-mono font-semibold" style={{color:'#60a5fa'}}>{selected.relatedAsset.asset_number}</span>
                      <span style={{color:'#334155'}}> — {selected.relatedAsset.brand} {selected.relatedAsset.model}</span>
                    </div>
                  )}
                  <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Issue Description</label>
                    <textarea value={form.repair_issue||''} onChange={e=>setForm({...form,repair_issue:e.target.value})} rows={2} className="input-dark" placeholder="Describe the repair issue..."/></div>
                  <div className="grid grid-cols-2 gap-3">
                    <FF label="Repair Vendor" name="repair_vendor" form={form} setForm={setForm} placeholder="e.g. IT Department"/>
                    <FF label="Repair Cost (LKR)" name="estimated_repair_cost" type="number" form={form} setForm={setForm} placeholder="0.00"/>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Status</label>
                      <select value={form.repair_status||'in_progress'} onChange={e=>setForm({...form,repair_status:e.target.value})} className="input-dark">
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="pending">Pending</option>
                        <option value="warranty_claim">Warranty Claim</option>
                        <option value="cannot_repair">Cannot Repair</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                      <input type="checkbox" id="repair_warranty" checked={!!form.repair_warranty_covered} onChange={e=>setForm({...form,repair_warranty_covered:e.target.checked})} className="accent-purple-500"/>
                      <label htmlFor="repair_warranty" className="text-xs cursor-pointer" style={{color:'#94a3b8'}}>Warranty Covered</label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Add to Damages ── */}
            <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(239,68,68,0.25)'}}>
              <label className="flex items-center gap-2 cursor-pointer px-4 py-3" style={{background:'rgba(239,68,68,0.08)'}}>
                <input type="checkbox" checked={!!form.is_physical_damage} onChange={e=>setForm({...form,is_physical_damage:e.target.checked})} className="accent-red-500"/>
                <AlertCircle size={14} style={{color:'#f87171'}}/> <span className="text-sm font-medium" style={{color:'#f87171'}}>Add to Damage Costs</span>
              </label>
              {form.is_physical_damage && (
                <div className="px-4 pb-4 pt-3 space-y-3" style={{background:'rgba(239,68,68,0.03)'}}>
                  {/* Read-only employee + asset */}
                  <div className="px-3 py-2 rounded-lg text-xs" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                    {selected?.reportedBy && <><span style={{color:'#475569'}}>Employee: </span><span style={{color:'#e2e8f0'}}>{selected.reportedBy.full_name}</span></>}
                    {selected?.relatedAsset && <><span style={{color:'#475569'}}> · Asset: </span><span className="font-mono" style={{color:'#60a5fa'}}>{selected.relatedAsset.asset_number}</span></>}
                  </div>
                  <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Damage Description</label>
                    <textarea value={form.damage_description||''} onChange={e=>setForm({...form,damage_description:e.target.value})} rows={2} className="input-dark" placeholder="Describe the physical damage..."/></div>
                  <div className="grid grid-cols-2 gap-3">
                    <FF label="Repair Cost (LKR) *" name="damage_repair_cost" type="number" form={form} setForm={setForm} placeholder="0.00"/>
                    <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Payment Method</label>
                      <select value={form.damage_payment_method||'user_pay'} onChange={e=>setForm({...form,damage_payment_method:e.target.value})} className="input-dark">
                        <option value="user_pay">User Pay (one-off)</option>
                        <option value="salary_deduction">Salary Deduction</option>
                        <option value="installment">Installment Plan</option>
                        <option value="company_pay">Company Pays (waived)</option>
                      </select>
                    </div>
                  </div>
                  <div className="px-3 py-2 rounded-lg text-xs" style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.15)',color:'#fca5a5'}}>
                    {form.damage_payment_method === 'company_pay'
                      ? '⚠ Damage record will be created and marked as Waived (company absorbs cost).'
                      : '⚠ Damage record will be created as Pending — employee is liable for payment.'}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>{saving?'Resolving...':'Mark Resolved'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── VIEW TICKET DETAILS ── */}
      {modal === 'view' && selected && (
        <Modal title={`Ticket Details: ${selected.ticket_number}`} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <div className="space-y-4 text-sm">

            {/* Header strip */}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge value={selected.status}/>
              <Badge value={selected.priority}/>
              <span className="text-xs font-mono" style={{color:'#60a5fa'}}>{selected.ticket_number}</span>
              <span className="text-xs" style={{color:'#475569'}}>{selected.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
            </div>

            {/* Title + description */}
            <div className="rounded-xl p-4" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="font-semibold mb-2" style={{color:'#e2e8f0'}}>{selected.title}</div>
              <div className="text-xs whitespace-pre-wrap" style={{color:'#94a3b8'}}>{selected.description||'—'}</div>
            </div>

            {/* Ticket info grid */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[
                ['Reported By', selected.reportedBy?.full_name||'—'],
                ['Reported Date', selected.reported_date ? new Date(selected.reported_date).toLocaleDateString('en-GB') : '—'],
                ['Asset', selected.relatedAsset?.asset_number||'—'],
                ['Estimated Days', selected.estimated_days||'—'],
              ].map(([k,v]) => (
                <div key={k} className="rounded-lg p-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.05)'}}>
                  <div style={{color:'#475569'}}>{k}</div>
                  <div className="font-medium mt-1" style={{color:'#e2e8f0'}}>{v}</div>
                </div>
              ))}
              {/* Response time */}
              <div className="col-span-2 rounded-lg p-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.05)'}}>
                <div style={{color:'#475569'}}>Response Time</div>
                <div className="mt-1"><ElapsedBadge ticket={selected}/></div>
              </div>
            </div>

            {/* Resolution details */}
            {(selected.resolution_notes || selected.root_cause || selected.resolved_date) && (
              <div className="rounded-xl p-4" style={{background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.2)'}}>
                <div className="text-xs font-semibold mb-3" style={{color:'#34d399'}}>Resolution Details</div>
                <div className="space-y-2 text-xs">
                  {selected.resolved_date && <div><span style={{color:'#475569'}}>Resolved On: </span><span style={{color:'#e2e8f0'}}>{new Date(selected.resolved_date).toLocaleDateString('en-GB')}</span></div>}
                  {selected.resolution_notes && <div><span style={{color:'#475569'}}>Resolution: </span><span className="whitespace-pre-wrap" style={{color:'#e2e8f0'}}>{selected.resolution_notes}</span></div>}
                  {selected.root_cause && <div><span style={{color:'#475569'}}>Root Cause: </span><span className="whitespace-pre-wrap" style={{color:'#e2e8f0'}}>{selected.root_cause}</span></div>}
                </div>
              </div>
            )}

            {/* Damage / payment details */}
            {selected.is_physical_damage && (
              <div className="rounded-xl p-4" style={{background:'rgba(239,68,68,0.07)',border:'1px solid rgba(239,68,68,0.2)'}}>
                <div className="text-xs font-semibold mb-3" style={{color:'#f87171'}}>Physical Damage Record</div>
                <div className="space-y-2 text-xs">
                  {selected.damage_description && <div><span style={{color:'#475569'}}>Description: </span><span style={{color:'#e2e8f0'}}>{selected.damage_description}</span></div>}
                  {selected.damage_repair_cost && <div><span style={{color:'#475569'}}>Repair Cost: </span><span style={{color:'#f87171'}}>LKR {Number(selected.damage_repair_cost).toLocaleString()}</span></div>}
                  {selected.damage_payment_method && <div><span style={{color:'#475569'}}>Paid By: </span><span style={{color:'#e2e8f0'}}>{selected.damage_payment_method.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</span></div>}
                </div>
              </div>
            )}

            {/* Close details */}
            {selected.status === 'closed' && (
              <div className="rounded-xl p-4" style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.2)'}}>
                <div className="text-xs font-semibold mb-3" style={{color:'#60a5fa'}}>Closure Details</div>
                <div className="space-y-2 text-xs">
                  {selected.closed_date && <div><span style={{color:'#475569'}}>Closed On: </span><span style={{color:'#e2e8f0'}}>{new Date(selected.closed_date).toLocaleDateString('en-GB')}</span></div>}
                  {selected.damage_remarks && <div><span style={{color:'#475569'}}>Closing Remarks: </span><span className="whitespace-pre-wrap" style={{color:'#e2e8f0'}}>{selected.damage_remarks}</span></div>}
                  {!selected.damage_remarks && <div style={{color:'#334155'}}>No closing remarks recorded.</div>}
                </div>
              </div>
            )}

            {/* Event timeline — what happened when */}
            <div className="rounded-xl p-4" style={{background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="text-xs font-semibold mb-3" style={{color:'#94a3b8'}}>Timeline — what happened when</div>
              <EventTimeline events={timeline} loading={timelineLoading}/>
            </div>

            <div className="flex justify-end">
              <button onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost px-6">Close</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── CLOSE TICKET ── */}
      {/* ── BOARD APPROVAL (admin only) ─────────────── */}
      {modal === 'board' && selected && (
        <Modal title={`Board Approval — ${selected.ticket_number}`} onClose={() => { setModal(null); setSelected(null); setBoardInfo(null); }} size="lg">
          <div className="space-y-4">
            <div className="rounded-xl p-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="text-sm font-medium" style={{color:'#e2e8f0'}}>{selected.title}</div>
              <div className="text-xs mt-1" style={{color:'#475569'}}>
                {selected.reportedBy?.full_name || '—'} · {selected.category?.replace(/_/g,' ')} · <Badge value={selected.status}/>
              </div>
            </div>

            {/* Shared board-approval panel — identical to IT Accessory Requests */}
            <BoardApprovalPanel
              basePath="/tickets"
              recordId={selected.id}
              info={boardInfo}
              heading={false}
              itemLabel="ticket"
              onChange={(info, meta) => {
                setBoardInfo(info);
                if (info?.status === 'pending') {
                  setSelected(sel => ({ ...sel, status: 'board_approval', board_approval_status: 'pending' }));
                } else if (meta?.restoredStatus) {
                  // Request withdrawn — the ticket is back to the status it had before
                  setSelected(sel => ({ ...sel, status: meta.restoredStatus, board_approval_status: null }));
                }
                load();
              }}
            />

            <button type="button" onClick={() => { setModal(null); setSelected(null); setBoardInfo(null); }}
              className="btn-ghost w-full justify-center">Close</button>
          </div>
        </Modal>
      )}

      {modal === 'close' && selected && (
        <Modal title={`Close Ticket: ${selected.ticket_number}`} onClose={() => { setModal(null); setSelected(null); }} size="md">
          <form onSubmit={handleClose} className="space-y-4">
            <ErrMsg msg={error}/>
            <div className="px-4 py-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{selected.title}</div>
              <div className="text-xs mt-1" style={{color:'#475569'}}>
                {selected.reportedBy?.full_name||'—'}
                {selected.relatedAsset && <> · <span className="font-mono" style={{color:'#60a5fa'}}>{selected.relatedAsset.asset_number}</span></>}
              </div>
            </div>
            <FF label="Closing Remarks" name="remarks" form={form} setForm={setForm} rows={3}
              placeholder="Add any closing notes or feedback from the user..."/>
            <div className="px-4 py-3 rounded-xl text-xs" style={{background:'rgba(59,130,246,0.07)',border:'1px solid rgba(59,130,246,0.15)',color:'#60a5fa'}}>
              A closure email will be sent to the user with ticket details and your remarks.
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center"
                style={{background:'linear-gradient(135deg,#3b82f6,#1d4ed8)'}}>
                {saving ? 'Closing...' : 'Close Ticket & Send Email'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── ASSIGN PENDING REVIEW TICKET ── */}
      {assignModal && (
        <Modal title={`Assign Ticket: ${assignModal.ticket_number}`} onClose={closeAssign} size="sm">
          <div className="space-y-4">
            <div className="rounded-xl p-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="text-sm font-medium mb-1" style={{color:'#e2e8f0'}}>{assignModal.title}</div>
              <div className="text-xs" style={{color:'#475569'}}>Reported by: {assignModal.reportedBy?.full_name||'—'}</div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Assign to IT Staff *</label>
              <SearchableSelect value={assignTo} onChange={setAssignTo}
                options={staffOptions(itStaffList)} placeholder="Select IT staff member..."/>
            </div>

            {/* Send it to the board straight from here — no need to assign,
                reopen the ticket and start the approval separately. */}
            {isAdmin && (
              <div className="rounded-xl p-3" style={{background:'rgba(139,92,246,0.06)',border:'1px solid rgba(139,92,246,0.2)'}}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={alsoApprove} onChange={e => setAlsoApprove(e.target.checked)} className="accent-purple-500"/>
                  <span className="text-xs font-semibold" style={{color:'#a78bfa'}}>
                    <Landmark size={11} style={{display:'inline',marginRight:4}}/>This needs board approval — send it now
                  </span>
                </label>
                {alsoApprove && (
                  <div className="space-y-2.5 mt-3">
                    <div>
                      <label className="block text-xs mb-1" style={{color:'#475569'}}>Estimation Cost (LKR) *</label>
                      <input type="number" min="0" step="0.01" value={approvalForm.estimation_cost}
                        onChange={e => setApprovalForm(f => ({ ...f, estimation_cost: e.target.value }))}
                        className="input-dark" placeholder="e.g. 25000"/>
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{color:'#475569'}}>Send Approval To *</label>
                      <input type="email" value={approvalForm.approver_email}
                        onChange={e => setApprovalForm(f => ({ ...f, approver_email: e.target.value }))}
                        className="input-dark" placeholder="approver@clsynergy.com"/>
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{color:'#475569'}}>Remark to Approver</label>
                      <textarea rows={2} value={approvalForm.admin_remark}
                        onChange={e => setApprovalForm(f => ({ ...f, admin_remark: e.target.value }))}
                        className="input-dark" placeholder="Optional context for the approver..."/>
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{color:'#475569'}}>Quotation (optional)</label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                        onChange={e => setApprovalForm(f => ({ ...f, quotation: e.target.files?.[0] || null }))}
                        className="input-dark text-xs"/>
                    </div>
                    <div className="text-xs" style={{color:'#334155'}}>
                      The ticket is assigned first, then the approval request goes out with one-time Approve / Reject links.
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={closeAssign} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={handleReviewAssign} disabled={!assignTo || assigning} className="btn-primary flex-1 justify-center"
                style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>
                <UserCheck size={13}/> {assigning ? 'Working...' : alsoApprove ? 'Assign & Send for Approval' : 'Assign & Notify'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── ESCALATE TO THE HEAD OF IT ── */}
      {escalateModal && (
        <Modal title={`Escalate ${escalateModal.ticket_number} for Higher Support`} onClose={() => { setEscalateModal(null); setEscalateReason(''); }} size="sm">
          <div className="space-y-4">
            <div className="rounded-xl p-3" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="text-sm font-medium mb-1" style={{color:'#e2e8f0'}}>{escalateModal.title}</div>
              <div className="text-xs" style={{color:'#475569'}}>Reported by: {escalateModal.reportedBy?.full_name||'—'}</div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Why do you need higher support? *</label>
              <textarea rows={3} value={escalateReason} onChange={e => setEscalateReason(e.target.value)}
                className="input-dark" placeholder="e.g. Motherboard fault — needs vendor RMA approval"/>
            </div>
            <div className="rounded-xl p-3 text-xs" style={{background:'rgba(139,92,246,0.07)',border:'1px solid rgba(139,92,246,0.22)',color:'#a78bfa'}}>
              The ticket moves to the Head of IT, its priority is raised to High, and the employee and their HOD are emailed
              that it has been escalated for higher-level support. The ticket stays open.
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setEscalateModal(null); setEscalateReason(''); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button onClick={handleEscalate} disabled={escalating} className="btn-primary flex-1 justify-center"
                style={{background:'linear-gradient(135deg,#8b5cf6,#6d28d9)'}}>
                <ArrowUpCircle size={13}/> {escalating ? 'Escalating...' : 'Escalate to Head of IT'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
